import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  InputNumber, 
  Switch, 
  Space, 
  Typography, 
  Alert, 
  Tooltip,
  Popconfirm,
  message,
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ArrowUpOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { 
  PlanogramTemplate, 
  MilestoneConfig, 
  PlanType, 
  ResponsibleTeam, 
  ValidationError,
  MilestoneType
} from '../../../../types/plan-template';
import { DEFAULT_MILESTONE_CONFIGS, MILESTONE_BUSINESS_RULES } from '../../../../types/plan-template';
import { ReferenceDataService } from '../../../../services/referenceDataService';

const { Title, Text } = Typography;
const { Option } = Select;

// 从ResponsibleTeam类型中获取选项
const responsibleTeamOptions = [
  'Planogram Team',
  'Construction Team',
  'Commercial Team',
  'Marketing Team',
  'Operation Team',
  'IT Team',
] as const satisfies readonly ResponsibleTeam[];

interface ValidationSummaryProps {
  milestones: MilestoneConfig[];
}

const ValidationSummary: React.FC<ValidationSummaryProps> = ({ milestones }) => {
  const { t } = useTranslation();
  
  const validateMilestoneRules = () => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const successes: string[] = [];

    // Check minimum milestone count
    if (milestones.length < 2) {
      errors.push(t('planTemplate.wizard.milestoneConfiguration.validation.minMilestones'));
    } else {
      successes.push(t('planTemplate.wizard.milestoneConfiguration.validation.milestoneCountValid', { count: milestones.length }));
    }

    // Check maximum milestone count
    if (milestones.length > 9) {
      errors.push(t('planTemplate.wizard.milestoneConfiguration.validation.maxMilestones'));
    }

    // Check final milestone
    const finalMilestone = milestones.find(m => m.isFinalMilestone);
    if (!finalMilestone) {
      errors.push(t('planTemplate.wizard.milestoneConfiguration.validation.finalMilestoneRequired'));
    } else {
      successes.push(t('planTemplate.wizard.milestoneConfiguration.validation.finalMilestoneCorrect'));
    }

    // Check sequence uniqueness
    const sequences = milestones.map(m => m.sequence);
    const uniqueSequences = new Set(sequences);
    if (sequences.length !== uniqueSequences.size) {
      errors.push(t('planTemplate.wizard.milestoneConfiguration.validation.sequenceUnique'));
    } else {
      successes.push(t('planTemplate.wizard.milestoneConfiguration.validation.sequenceConfigCorrect'));
    }

    // Check duration and buffer time
    const invalidDurations = milestones.filter(m => 
      m.durationEstimate < 1 || m.durationEstimate > 365 ||
      m.bufferTime < 0 || m.bufferTime > 30
    );
    if (invalidDurations.length > 0) {
      errors.push(t('planTemplate.wizard.milestoneConfiguration.validation.invalidDurationOrBuffer', { count: invalidDurations.length }));
    } else {
      successes.push(t('planTemplate.wizard.milestoneConfiguration.validation.durationAndBufferReasonable'));
    }

    // Check parallel execution
    const parallelMilestones = milestones.filter(m => m.parallelExecution);
    if (parallelMilestones.length > 0) {
      const parallelGroups = new Map<number, MilestoneConfig[]>();
      parallelMilestones.forEach(m => {
        if (!parallelGroups.has(m.sequence)) {
          parallelGroups.set(m.sequence, []);
        }
        parallelGroups.get(m.sequence)!.push(m);
      });

      parallelGroups.forEach((group, sequence) => {
        if (group.length > 1) {
          const teams = new Set(group.map(m => m.responsibleTeam));
          if (teams.size > 1) {
            warnings.push(t('planTemplate.wizard.milestoneConfiguration.validation.parallelTeamConflict', { sequence }));
          }
        }
      });

      successes.push(t('planTemplate.wizard.milestoneConfiguration.validation.parallelExecutionSupported', { count: parallelMilestones.length }));
    }

    // Check team distribution
    const teamDistribution = new Map<ResponsibleTeam, number>();
    milestones.forEach(m => {
      teamDistribution.set(m.responsibleTeam, (teamDistribution.get(m.responsibleTeam) || 0) + 1);
    });

    if (teamDistribution.size === 1) {
      warnings.push(t('planTemplate.wizard.milestoneConfiguration.validation.singleTeamWarning'));
    } else {
      successes.push(t('planTemplate.wizard.milestoneConfiguration.validation.teamDistributionReasonable', { count: teamDistribution.size }));
    }

    return { errors, warnings, successes };
  };

  const { errors, warnings, successes } = validateMilestoneRules();

  return (
    <div>
      {errors.length > 0 && (
        <Alert
          type="error"
          message={t('planTemplate.wizard.milestoneConfiguration.validation.foundErrors', { count: errors.length })}
          description={
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 8 }}
        />
      )}

      {warnings.length > 0 && (
        <Alert
          type="warning"
          message={t('planTemplate.wizard.milestoneConfiguration.validation.suggestions', { count: warnings.length })}
          description={
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 8 }}
        />
      )}

      {successes.length > 0 && errors.length === 0 && (
        <Alert
          type="success"
          message={t('planTemplate.wizard.milestoneConfiguration.validation.validationPassed')}
          description={
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              {successes.map((success, index) => (
                <li key={index}>{success}</li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );
};

interface MilestoneConfigurationProps {
  planType?: PlanogramTemplate['planType'];
  milestones: MilestoneConfig[];
  onMilestonesChange: (milestones: MilestoneConfig[]) => void;
  onValidationChange: (isValid: boolean, errors?: ValidationError[]) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  mode?: 'create' | 'edit';
}

interface MilestoneRowProps {
  milestone: MilestoneConfig;
  index: number;
  total: number;
  onEdit: (milestone: MilestoneConfig) => void;
  onDelete: (milestoneId: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}

const MilestoneRow: React.FC<MilestoneRowProps> = ({
  milestone,
  index,
  total,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown
}) => {
  const { t } = useTranslation();
  
  const getResponsibleTeamColor = (team: ResponsibleTeam) => {
    const colors: Record<ResponsibleTeam, string> = {
      'Planogram Team': 'blue',
      'Construction Team': 'orange',
      'Commercial Team': 'green',
      'Marketing Team': 'red',
      'Operation Team': 'cyan',
      'IT Team': 'magenta'
    };
    return colors[team] || 'default';
  };

  return (
    <tr style={{ backgroundColor: milestone.isFinalMilestone ? '#f6ffed' : undefined }}>
      <td style={{ textAlign: 'center', width: 80 }}>
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0 || milestone.isFinalMilestone}
            onClick={() => onMoveUp(index)}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === total - 3 || milestone.isFinalMilestone}
            onClick={() => onMoveDown(index)}
          />
        </Space>
      </td>
      <td style={{ textAlign: 'center', width: 60 }}>
        <Tag color={milestone.isFinalMilestone ? 'success' : 'default'}>
          {milestone.sequence}
        </Tag>
      </td>
      <td>
        <div>
          <Text strong>{milestone.name}</Text>
          {milestone.isFinalMilestone && (
            <Tag color="success" style={{ marginLeft: 8 }}>
              {t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.finalMilestone')}
            </Tag>
          )}
        </div>
      </td>
      <td>
        <Tag color={getResponsibleTeamColor(milestone.responsibleTeam)}>
          {milestone.responsibleTeam}
        </Tag>
      </td>
      <td style={{ textAlign: 'center' }}>
        {milestone.durationEstimate} {t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.days')}
      </td>
      <td style={{ textAlign: 'center' }}>
        {milestone.bufferTime} {t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.days')}
      </td>
      <td style={{ textAlign: 'center' }}>
        {milestone.parallelExecution ? (
          <Tag color="processing">{t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.parallel')}</Tag>
        ) : (
          <Tag>{t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.serial')}</Tag>
        )}
      </td>
      <td style={{ textAlign: 'center', width: 120 }}>
        <Space size="small">
          <Tooltip title={t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.editTooltip')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(milestone)}
            />
          </Tooltip>
          {!milestone.isFinalMilestone && (
            <Popconfirm
              title={t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.deleteConfirm')}
              onConfirm={() => onDelete(milestone.id)}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
            >
              <Tooltip title={t('planTemplate.wizard.milestoneConfiguration.deleteMilestone')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      </td>
    </tr>
  );
};

// 计算考虑并行和串行逻辑的总耗时
const calculateTotalDuration = (milestones: MilestoneConfig[]): number => {
  if (milestones.length === 0) return 0;
  
  let totalDays = 0;
  let parallelGroup: MilestoneConfig[] = [];
  
  for (let i = 0; i < milestones.length; i++) {
    const milestone = milestones[i];
    
    if (milestone.parallelExecution) {
      // 如果是并行的，加入当前并行组
      parallelGroup.push(milestone);
    } else {
      // 如果是串行的，先处理之前的并行组
      if (parallelGroup.length > 0) {
        // 取并行组中耗时最大的
        const maxDuration = Math.max(
          ...parallelGroup.map(m => m.durationEstimate + m.bufferTime)
        );
        totalDays += maxDuration;
        // 清空并行组
        parallelGroup = [];
      }
      // 加上当前串行里程碑的耗时
      totalDays += milestone.durationEstimate + milestone.bufferTime;
    }
  }
  
  // 处理最后可能剩余的并行组
  if (parallelGroup.length > 0) {
    const maxDuration = Math.max(
      ...parallelGroup.map(m => m.durationEstimate + m.bufferTime)
    );
    totalDays += maxDuration;
  }
  
  return totalDays;
};

const MilestoneConfiguration: React.FC<MilestoneConfigurationProps> = ({
  planType,
  milestones,
  onMilestonesChange,
  onValidationChange,
  onNext,
  onPrevious,
  mode
}) => {
  const { t } = useTranslation();
  const [availableMilestoneTypes, setAvailableMilestoneTypes] = useState<MilestoneType[]>([]);
  const [editingMilestone, setEditingMilestone] = useState<MilestoneConfig | null>(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Load default milestones when plan type changes
  useEffect(() => {
    if (planType && milestones.length === 0) {
      loadDefaultMilestones();
    }
  }, [planType]);

  // Load available milestone types
  useEffect(() => {
    loadAvailableMilestoneTypes();
  }, []);

  // Validate milestones whenever they change
  useEffect(() => {
    validateMilestones();
  }, [milestones]);

  const loadDefaultMilestones = () => {
    if (!planType) return;

    const defaultConfigs = DEFAULT_MILESTONE_CONFIGS[planType] || [];
    const defaultMilestones: MilestoneConfig[] = defaultConfigs.map((config, index) => ({
      id: `${config.sequence}`,
      code: config.code || `MS_${index + 1}`,
      name: config.name || `Milestone ${index + 1}`,
      description: config.description || '',
      sequence: config.sequence || index + 1,
      responsibleTeam: config.responsibleTeam || 'Planogram Team',
      prerequisites: [],
      deliverables: config.deliverables || [],
      durationEstimate: config.durationEstimate || 1,
      bufferTime: config.bufferTime || 0,
      parallelExecution: config.parallelExecution || false,
      isFinalMilestone: config.isFinalMilestone || false
    }));

    onMilestonesChange(defaultMilestones);
  };

  const loadAvailableMilestoneTypes = async () => {
    try {
      const response = await ReferenceDataService.getMilestoneTypes();
      if (response.success) {
        setAvailableMilestoneTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to load milestone types:', error);
    }
  };

  const validateMilestones = useCallback(() => {
    const errors: ValidationError[] = [];

    // Check minimum milestone count
    if (milestones.length < 2) {
      errors.push({
        field: 'milestones',
        message: t('planTemplate.wizard.milestoneConfiguration.validation.minMilestones'),
        type: 'error'
      });
    }

    // Check maximum milestone count
    if (milestones.length > 9) {
      errors.push({
        field: 'milestones',
        message: t('planTemplate.wizard.milestoneConfiguration.validation.maxMilestones'),
        type: 'error'
      });
    }

    // Check final milestone
    const finalMilestone = milestones.find(m => m.isFinalMilestone);
    if (!finalMilestone) {
      errors.push({
        field: 'milestones',
        message: t('planTemplate.wizard.milestoneConfiguration.validation.finalMilestoneRequired'),
        type: 'error'
      });
    } 
    // else if (finalMilestone.name !== 'Store Task Generation') {
    //   errors.push({
    //     field: 'milestones',
    //     message: t('planTemplate.wizard.milestoneConfiguration.validation.finalMilestoneMustBeStoreTask'),
    //     type: 'error'
    //   });
    // }

    // Check sequence uniqueness
    const sequences = milestones.map(m => m.sequence);
    const uniqueSequences = new Set(sequences);
    if (sequences.length !== uniqueSequences.size) {
      errors.push({
        field: 'milestones',
        message: t('planTemplate.wizard.milestoneConfiguration.validation.sequenceUnique'),
        type: 'error'
      });
    }

    // Apply business rules
    MILESTONE_BUSINESS_RULES.forEach(rule => {
      if (rule.category === 'milestone') {
        const result = rule.validator(milestones);
        if (!result.isValid && result.message) {
          errors.push({
            field: 'milestones',
            message: result.message,
            type: 'error'
          });
        }
      }
    });

    const isValid = errors.length === 0;
    onValidationChange(isValid, errors);
  }, [milestones, onValidationChange]);

  const handleAddMilestone = () => {
    form.resetFields();
    setAddModalVisible(true);
  };

  const handleEditMilestone = (milestone: MilestoneConfig) => {
    setEditingMilestone(milestone);
    form.setFieldsValue(milestone);
    setEditModalVisible(true);
  };

  const handleDeleteMilestone = (milestoneId: string) => {
    const updatedMilestones = milestones.filter(m => m.id !== milestoneId);
    // Resequence milestones
    const resequencedMilestones = updatedMilestones.map((m, index) => ({
      ...m,
      sequence: index + 1
    }));
    onMilestonesChange(resequencedMilestones);
    message.success(t('planTemplate.wizard.milestoneConfiguration.deleteMilestone') + t('common.success'));
  };

  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    
    const updatedMilestones = [...milestones];
    [updatedMilestones[index - 1], updatedMilestones[index]] = 
    [updatedMilestones[index], updatedMilestones[index - 1]];
    
    // Update sequences
    const resequencedMilestones = updatedMilestones.map((m, idx) => ({
      ...m,
      sequence: idx + 1
    }));
    
    onMilestonesChange(resequencedMilestones);
  }, [milestones, onMilestonesChange]);

  const handleMoveDown = useCallback((index: number) => {
    if (index === milestones.length - 1) return;
    
    const updatedMilestones = [...milestones];
    [updatedMilestones[index], updatedMilestones[index + 1]] = 
    [updatedMilestones[index + 1], updatedMilestones[index]];
    
    // Update sequences
    const resequencedMilestones = updatedMilestones.map((m, idx) => ({
      ...m,
      sequence: idx + 1
    }));
    
    onMilestonesChange(resequencedMilestones);
  }, [milestones, onMilestonesChange]);

  const handleAddModalOk = async () => {
    try {
      const values = await form.validateFields();
      const newMilestone: MilestoneConfig = {
        id: `${Date.now()}`,
        code: values.code,
        name: values.name,
        description: values.description || '',
        sequence: milestones.length + 1,
        responsibleTeam: values.responsibleTeam,
        prerequisites: [],
        deliverables: values.deliverables || [],
        durationEstimate: values.durationEstimate,
        bufferTime: values.bufferTime || 0,
        parallelExecution: values.parallelExecution || false,
        isFinalMilestone: false
      };

      onMilestonesChange([...milestones, newMilestone]);
      setAddModalVisible(false);
      message.success(t('planTemplate.wizard.milestoneConfiguration.addMilestone') + t('common.success'));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleEditModalOk = async () => {
    if (!editingMilestone) return;

    try {
      const values = await form.validateFields();
      const updatedMilestones = milestones.map(m => 
        m.id === editingMilestone.id 
          ? { ...m, ...values }
          : m
      );

      onMilestonesChange(updatedMilestones);
      setEditModalVisible(false);
      setEditingMilestone(null);
      message.success(t('planTemplate.wizard.milestoneConfiguration.editMilestone') + t('common.success'));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleMilestoneTypeSelect = (milestoneTypeId: string) => {
    const milestoneType = availableMilestoneTypes.find(mt => mt.id === milestoneTypeId);
    if (milestoneType) {
      form.setFieldsValue({
        code: milestoneType.code,
        name: milestoneType.name,
        description: milestoneType.description,
        responsibleTeam: milestoneType.defaultResponsibleTeam,
        durationEstimate: milestoneType.defaultDuration,
        bufferTime: milestoneType.defaultBufferTime,
        parallelExecution: milestoneType.canBeParallel,
        deliverables: milestoneType.deliverables
      });
    }
  };



  const columns = [
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.moveUp'), key: 'sort', width: 80 },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.sequence'), key: 'sequence', width: 60 },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.name'), key: 'name' },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.team'), key: 'responsibleTeam', width: 120 },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.duration'), key: 'duration', width: 100 },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.buffer'), key: 'buffer', width: 100 },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.execution'), key: 'execution', width: 100 },
    { title: t('planTemplate.wizard.milestoneConfiguration.milestoneDetails.actions'), key: 'actions', width: 120 }
  ];

  return (
    <div style={{ padding: '0 24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>{t('planTemplate.wizard.milestoneConfiguration.title')}</Title>
        <Text type="secondary">
          {t('planTemplate.wizard.milestoneConfiguration.description')}
          {planType && ` ${t('planTemplate.wizard.basicInfo.currentPlanType')}：${planType}`}
        </Text>
      </div>

      {milestones.length === 0 && planType && (
        <Alert
          message={t('planTemplate.wizard.milestoneConfiguration.noMilestones')}
          description={
            <div>
              <p>{t('planTemplate.wizard.milestoneConfiguration.noMilestonesDesc')}</p>
              <Button 
                type="primary" 
                onClick={loadDefaultMilestones}
                style={{ marginTop: 8 }}
              >
                {t('planTemplate.wizard.milestoneConfiguration.loadDefaults')}
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('planTemplate.wizard.milestoneConfiguration.milestoneList')} ({milestones.length}/9)</span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddMilestone}
              disabled={milestones.length >= 9}
            >
              {t('planTemplate.wizard.milestoneConfiguration.addMilestone')}
            </Button>
          </div>
        }
        style={{ marginBottom: 24 }}
      >
        {milestones.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {columns.map(col => (
                    <th 
                      key={col.key}
                      style={{ 
                        padding: '12px 8px',
                        textAlign: ['sequence', 'duration', 'buffer', 'execution', 'actions', 'sort'].includes(col.key) ? 'center' : 'left',
                        fontWeight: 600,
                        color: '#262626',
                        width: col.width
                      }}
                    >
                      {col.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {milestones.map((milestone, index) => (
                  <MilestoneRow
                    key={milestone.id}
                    milestone={milestone}
                    index={index}
                    total={milestones.length}
                    onEdit={handleEditMilestone}
                    onDelete={handleDeleteMilestone}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 0',
            color: '#999'
          }}>
            <InfoCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>{t('planTemplate.wizard.milestoneConfiguration.noMilestonesConfigured')}</div>
            <div style={{ fontSize: '14px', marginTop: 8 }}>
              {t('planTemplate.wizard.milestoneConfiguration.clickToStartConfig')}
            </div>
          </div>
        )}
      </Card>

      {/* Milestone Statistics */}
      {milestones.length > 0 && (
        <Card title={t('planTemplate.wizard.milestoneConfiguration.statistics.title')} size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <Text type="secondary">{t('planTemplate.wizard.milestoneConfiguration.statistics.totalMilestones')}：</Text>
              <Text strong>{milestones.length}</Text>
            </div>
            <div>
              <Text type="secondary">{t('planTemplate.wizard.milestoneConfiguration.statistics.parallelMilestones')}：</Text>
              <Text strong>{milestones.filter(m => m.parallelExecution).length}</Text>
            </div>
            <div>
              <Text type="secondary">{t('planTemplate.wizard.milestoneConfiguration.statistics.totalDuration')}：</Text>
              <Text strong>
                {calculateTotalDuration(milestones)} {t('planTemplate.wizard.milestoneConfiguration.statistics.days')}
              </Text>
            </div>
            <div>
              <Text type="secondary">{t('planTemplate.wizard.milestoneConfiguration.statistics.involvedTeams')}：</Text>
              <Text strong>
                {new Set(milestones.map(m => m.responsibleTeam)).size} {t('planTemplate.wizard.milestoneConfiguration.statistics.teams')}
              </Text>
            </div>
          </div>
        </Card>
      )}

      {/* Business Rules Info */}
      <Card title={t('planTemplate.wizard.milestoneConfiguration.businessRules.title')} size="small" style={{ marginBottom: 24 }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>{t('planTemplate.wizard.milestoneConfiguration.businessRules.milestoneCountRule')}</li>
          <li>{t('planTemplate.wizard.milestoneConfiguration.businessRules.finalMilestoneRule')}</li>
          <li>{t('planTemplate.wizard.milestoneConfiguration.businessRules.sequenceRule')}</li>
          <li>{t('planTemplate.wizard.milestoneConfiguration.businessRules.parallelExecutionRule')}</li>
          <li>{t('planTemplate.wizard.milestoneConfiguration.businessRules.durationRule')}</li>
          <li>{t('planTemplate.wizard.milestoneConfiguration.businessRules.teamCompatibilityRule')}</li>
        </ul>
      </Card>

      {/* Validation Summary */}
      {milestones.length > 0 && (
        <Card title={t('planTemplate.wizard.milestoneConfiguration.validation.title')} size="small" style={{ marginBottom: 24 }}>
          <ValidationSummary milestones={milestones} />
        </Card>
      )}



      {/* Add Milestone Modal */}
      <Modal
        title={t('planTemplate.wizard.milestoneConfiguration.addMilestone')}
        open={addModalVisible}
        onOk={handleAddModalOk}
        onCancel={() => setAddModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            durationEstimate: 1,
            bufferTime: 0,
            parallelExecution: false
          }}
        >
          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.selectMilestoneType')}
            name="milestoneTypeId"
            rules={[{ required: true, message: t('planTemplate.wizard.milestoneConfiguration.form.selectMilestoneTypeRequired') }]}
            extra={t('planTemplate.wizard.milestoneConfiguration.form.selectMilestoneTypeHelp')}
          >
            <Select
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.selectMilestoneTypePlaceholder')}
              onChange={handleMilestoneTypeSelect}
              showSearch
              filterOption={(input, option) => {
                const type = availableMilestoneTypes.find(t => t.id === option?.value);
                return type ? 
                  type.name.toLowerCase().includes(input.toLowerCase()) ||
                  type.description.toLowerCase().includes(input.toLowerCase()) ||
                  type.category.toLowerCase().includes(input.toLowerCase())
                  : false;
              }}
            >
              {availableMilestoneTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.milestoneCode')}
            name="code"
            extra={t('planTemplate.wizard.milestoneConfiguration.form.milestoneCodeHelp')}
          >
            <Input 
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.milestoneCodePlaceholder')} 
              disabled={true}
              style={{ 
                backgroundColor: '#f5f5f5',
                color: '#666'
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.milestoneName')}
            name="name"
            extra={t('planTemplate.wizard.milestoneConfiguration.form.milestoneNameHelp')}
          >
            <Input 
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.milestoneNamePlaceholder')} 
              disabled={true}
              style={{ 
                backgroundColor: '#f5f5f5',
                color: '#666'
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.description')}
            name="description"
          >
            <Input.TextArea 
              rows={3}
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.responsibleTeam')}
            name="responsibleTeam"
            rules={[{ required: true, message: t('planTemplate.wizard.milestoneConfiguration.form.responsibleTeamRequired') }]}
          >
            <Select 
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.responsibleTeamPlaceholder')}
              disabled={mode === 'create' || mode === 'edit'}
              style={{
                backgroundColor: mode === 'create' || mode === 'edit' ? '#f5f5f5' : undefined,
                color: mode === 'create' || mode === 'edit' ? '#666' : undefined
              }}
            >
              {responsibleTeamOptions.map(team => (
                <Option key={team} value={team}>
                  {team}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              label={t('planTemplate.wizard.milestoneConfiguration.form.estimatedDuration')}
              name="durationEstimate"
              rules={[
                { required: true, message: t('planTemplate.wizard.milestoneConfiguration.form.estimatedDurationRequired') },
                { type: 'number', min: 1, max: 365, message: t('planTemplate.wizard.milestoneConfiguration.form.estimatedDurationRange') }
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} max={365} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={t('planTemplate.wizard.milestoneConfiguration.form.bufferTime')}
              name="bufferTime"
              rules={[
                { type: 'number', min: 0, max: 30, message: t('planTemplate.wizard.milestoneConfiguration.form.bufferTimeRange') }
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={30} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.executionMode')}
            name="parallelExecution"
            valuePropName="checked"
            extra={t('planTemplate.wizard.milestoneConfiguration.form.executionModeHelp')}
          >
            <Switch 
              checkedChildren={t('planTemplate.wizard.milestoneConfiguration.form.parallel')} 
              unCheckedChildren={t('planTemplate.wizard.milestoneConfiguration.form.serial')}
            />
          </Form.Item>



          <Form.Item
            label={t('planTemplate.wizard.milestoneConfiguration.form.deliverables')}
            name="deliverables"
          >
            <Select
              mode="tags"
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.deliverablesPlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Milestone Modal */}
      <Modal
        title={t('planTemplate.editMilestone')}
        open={editModalVisible}
        onOk={handleEditModalOk}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingMilestone(null);
        }}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label={t('planTemplate.milestoneCode')}
            name="code"
            extra={t('planTemplate.systemPresetCode')}
          >
            <Input 
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.milestoneCodePlaceholder')}
              disabled={true}
              style={{ 
                backgroundColor: '#f5f5f5',
                color: '#666'
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('planTemplate.milestoneName')}
            name="name"
            extra={t('planTemplate.systemPresetName')}
          >
            <Input 
              placeholder={t('planTemplate.wizard.milestoneConfiguration.form.milestoneNamePlaceholder')}
              disabled={true}
              style={{ 
                backgroundColor: '#f5f5f5',
                color: '#666'
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('planTemplate.description')}
            name="description"
          >
            <Input.TextArea 
              rows={3}
              placeholder={t('planTemplate.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={t('planTemplate.responsibleTeam')}
            name="responsibleTeam"
            rules={[{ required: true, message: t('planTemplate.selectTeamRequired') }]}
          >
            <Select 
              placeholder={t('planTemplate.selectTeam')}
              disabled={mode === 'create' || mode === 'edit'}
              style={{
                backgroundColor: mode === 'create' || mode === 'edit' ? '#f5f5f5' : undefined,
                color: mode === 'create' || mode === 'edit' ? '#666' : undefined
              }}
            >
              {responsibleTeamOptions.map(team => (
                <Option key={team} value={team}>
                  {team}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              label={t('planTemplate.estimatedDays')}
              name="durationEstimate"
              rules={[
                { required: true, message: t('planTemplate.estimatedDaysRequired') },
                { type: 'number', min: 1, max: 365, message: t('planTemplate.daysRange') }
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} max={365} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={t('planTemplate.bufferDays')}
              name="bufferTime"
              rules={[
                { type: 'number', min: 0, max: 30, message: t('planTemplate.bufferDaysRange') }
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={30} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item
            label={t('planTemplate.executionMode')}
            name="parallelExecution"
            valuePropName="checked"
            extra={t('planTemplate.executionModeHelp')}
          >
            <Switch 
              checkedChildren={t('planTemplate.parallel')} 
              unCheckedChildren={t('planTemplate.serial')}
              // disabled={editingMilestone?.isFinalMilestone}
            />
          </Form.Item>



          <Form.Item
            label={t('planTemplate.deliverables')}
            name="deliverables"
          >
            <Select
              mode="tags"
              placeholder={t('planTemplate.deliverablesPlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {editingMilestone?.isFinalMilestone && (
            <Alert
              message={t('planTemplate.finalMilestone')}
              description={t('planTemplate.finalMilestoneDesc')}
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default MilestoneConfiguration;